import { computed, h, inject, type ComputedRef } from 'vue'
import { useI18n } from 'vue-i18n'

import CFilesFolderDeleteDialogContent from '@lib/components/CFilesFolderDeleteDialogContent/CFilesFolderDeleteDialogContent.vue'
import type { ViewCompactFileFolder } from '@lib/types/files'
import type { BrowserAppProps } from '@browser/appTypes'
import type { FolderDeleter, FileDeleter } from '@browser/stores/api'
import { ActionKeys } from '@browser/types/actionKeys'
import { MODAL_WIDTH } from '@lib/constants/modal.const.ts'

export default function useDeleteUsedCanvasesConfirmation() {
  const { t } = useI18n()
  const appProps = inject<ComputedRef<BrowserAppProps>>(
    'appProps',
    computed(() => ({}) as BrowserAppProps),
  )
  const pitcherInfo = inject<ComputedRef<{ instanceId: string }>>(
    'pitcherInfo',
    computed(() => ({ instanceId: '' })),
  )

  async function getDeleteConfirmationConfig({
    item,
    deleteFunction,
    action,
    onAfterDelete,
  }: {
    item: ViewCompactFileFolder
    deleteFunction: FolderDeleter | FileDeleter
    action: ActionKeys.DeleteFile | ActionKeys.DeleteFolder
    onAfterDelete?: () => void
  }) {
    let relatedCanvases = 0

    if (action === ActionKeys.DeleteFile && appProps.value.onMetadataFilterFetch) {
      try {
        const _relatedCanvases = await appProps.value.onMetadataFilterFetch({
          key: 'pitcher_source_file_id',
          values: [item.id],
          is_section: true,
          is_template: false,
          is_block: false,
          return_arrays: true,
          instance_id: pitcherInfo.value.instanceId,
        })

        const canvasIds = Object.values(_relatedCanvases).flat()
        relatedCanvases = canvasIds.length
      } catch (error) {
        console.error('Failed to check for related canvases:', error)
      }
    }

    const hasRelatedCanvases = relatedCanvases > 0

    return {
      hasExit: false,
      ...(hasRelatedCanvases ? { style: { width: `${MODAL_WIDTH.Medium}px` } } : {}),
      title: t('canvasUI.browserApp.contextMenu.deleteDialog.title'),
      content: () =>
        h(CFilesFolderDeleteDialogContent, {
          name: item.name!,
          texts: {
            name: t('canvasUI.browserApp.contextMenu.deleteDialog.content.name'),
            info: hasRelatedCanvases
              ? t('canvasUI.browserApp.contextMenu.deleteDialog.content.infoWithSlides', { count: relatedCanvases })
              : t('canvasUI.browserApp.contextMenu.deleteDialog.content.info'),
            type: t('canvasUI.browserApp.contextMenu.deleteDialog.content.type'),
            ...(action === ActionKeys.DeleteFile
              ? { file: t('canvasUI.common.file') }
              : { folder: t('canvasUI.common.folder') }),
          },
        }),
      confirmText: hasRelatedCanvases
        ? t('canvasUI.browserApp.contextMenu.deleteDialog.confirmWithSlides')
        : t('canvasUI.browserApp.contextMenu.deleteDialog.confirm'),
      cancelText: t('canvasUI.browserApp.contextMenu.deleteDialog.cancel'),
      onConfirm: async () => {
        try {
          if (
            action === ActionKeys.DeleteFile &&
            appProps.value.onMetadataFilterFetch &&
            appProps.value.onDeleteCanvases
          ) {
            const relatedCanvases = await appProps.value.onMetadataFilterFetch({
              key: 'pitcher_source_file_id',
              values: [item.id],
              is_section: true,
              is_template: false,
              is_block: false,
              return_arrays: true,
              instance_id: pitcherInfo.value.instanceId,
            })

            await deleteFunction(item.id)
            const canvasIds = Object.values(relatedCanvases).flat()

            if (canvasIds.length > 0) {
              await appProps.value.onDeleteCanvases({ ids: canvasIds })
            }
          } else {
            await deleteFunction(item.id)
          }

          onAfterDelete?.()
        } catch (error) {
          console.error(`Failed to delete File/Folder: Error ${error}`)
          return
        }
      },
    }
  }

  return {
    getDeleteConfirmationConfig,
  }
}
